// ============================================================================
// Alarms Modals Module
// ============================================================================
// Modal for acknowledging alarms with optional notes

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { loadActiveAlarms } from './alarms.js';

// ============================================================================
// Acknowledge with Note Modal
// ============================================================================

export function showAcknowledgeNoteModal(eventId) {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.id = 'acknowledge-note-modal';

    modal.innerHTML = `
        <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div class="mt-3">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">${t('alarms.addNoteTitle')}</h3>
                    <button onclick="window.closeAcknowledgeNoteModal()" class="text-gray-400 hover:text-gray-500">
                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="mt-2">
                    <p class="text-sm text-gray-500 mb-3">
                        ${t('alarms.addNotePrompt')}
                    </p>
                    <textarea id="acknowledge-notes" rows="4" class="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="${t('alarms.notesPlaceholder')}"></textarea>
                </div>
                <div class="mt-4 flex justify-end space-x-3">
                    <button onclick="window.closeAcknowledgeNoteModal()" type="button" class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm">
                        ${t('common.cancel')}
                    </button>
                    <button onclick="window.acknowledgeAlarmEventWithNotes('${eventId}')" type="button" class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm">
                        ${t('alarms.acknowledgeWithNote')}
                    </button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);
    document.getElementById('acknowledge-notes').focus();
}

export function closeAcknowledgeNoteModal() {
    const modal = document.getElementById('acknowledge-note-modal');
    if (modal) {
        modal.remove();
    }
}

export async function acknowledgeAlarmEventWithNotes(eventId) {
    const notesTextarea = document.getElementById('acknowledge-notes');
    const notes = notesTextarea ? notesTextarea.value.trim() : '';

    try {
        const response = await fetch(`/api/alarm-events/${eventId}`, {
            method: 'PATCH',
            headers: { 
                'Authorization': `Bearer ${auth.token}`,
                'Content-Type': 'application/json' 
            },
            body: JSON.stringify({
                acknowledged: true,
                notes: notes || null
            })
        });

        if (response.ok) {
            closeAcknowledgeNoteModal();
            showSuccess(t('alarms.acknowledgeWithNoteSuccess'));
            loadActiveAlarms();
        } else {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const error = await response.json();
                console.error('Backend error detail:', error.detail);
                showError(error.detail || 'Failed to acknowledge alarm');
            } else {
                const errorText = await response.text();
                console.error('Backend error (non-JSON):', errorText);
                showError('Server error: ' + errorText.substring(0, 200));
            }
        }
    } catch (error) {
        console.error('Error acknowledging alarm:', error);
        showError('En fejl opstod under kvittering af alarmen');
    }
}

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.showAcknowledgeNoteModal = showAcknowledgeNoteModal;
window.closeAcknowledgeNoteModal = closeAcknowledgeNoteModal;
window.acknowledgeAlarmEventWithNotes = acknowledgeAlarmEventWithNotes;
